/**
 * THE TARGETING RULES, MIRRORED FROM THE ENGINE.
 *
 * 🚨 **WHY THIS FILE EXISTS, AND IT IS NOT "TAUNT WAS MISSING".** Alex, 2026-08-14:
 * *"aren't the cards supposed to protect the shield?"* - and then, correctly:
 * *"why did i have to whack a mole this. You are supposed to have a skill to follow the rules and
 * game play, features, mechanics."*
 *
 * The board skill enumerated the board's FURNITURE (17 elements) and the turn loop, so a missing
 * graveyard or clock would have been caught. **It never enumerated the RULES the client has to
 * mirror**, so there was nothing for taunt to be missing FROM. The keyword list has been sitting in
 * the served database the whole time: 146 keywords, of which a handful restrict what a player may
 * legally do. `tools/verify_mechanics_coverage.py` now fails the build on any restriction keyword
 * that is not accounted for below - that is the actual fix, because it does not rely on remembering.
 *
 * ⭐ **THE ENGINE IS THE AUTHORITY AND IT ALREADY REFUSES ILLEGAL ATTACKS** (`CheckTaunt` in
 * `Revival.Core`). So the game was never cheatable - what was broken is that the CLIENT offered
 * targets the server would reject, and a rejected command is swallowed silently. The player sees a
 * click that does nothing, which reads as a broken game rather than as an illegal move.
 *
 * ⛔ **THIS IS A MIRROR, NOT A SECOND RULES ENGINE.** It exists to decide what to OFFER and what to
 * grey out. It must never be trusted to decide an outcome, and when it disagrees with the server the
 * SERVER IS RIGHT. Every rule below is a transcription of the engine's own code, cited by name.
 */
import { EFFECT_KEYWORD } from "../effect-keywords.generated";
import type { MatchModel, RuntimeObject } from "./state";

/**
 * An entry in `RuntimeData.AppliedEffects[]`.
 *
 * ⚠️ `Disabled` IS THE HALF THAT GETS FORGOTTEN. The engine's test is
 * `HasAppliedNonDisabledEffect` - an effect that is still attached but disabled (Silence, Stasis)
 * does not apply. Reading presence alone would keep a silenced creature taunting.
 */
interface AppliedEffect {
  ConfigId?: string;
  Disabled?: boolean;
}

/** Does this object have a non-disabled effect with the given keyword, right now? */
export function hasKeyword(obj: RuntimeObject | undefined, keyword: string): boolean {
  if (!obj) return false;
  const applied = obj.AppliedEffects as AppliedEffect[] | undefined;
  if (!Array.isArray(applied)) return false;
  return applied.some(
    (e) => !e?.Disabled && !!e?.ConfigId && EFFECT_KEYWORD[e.ConfigId] === keyword,
  );
}

/**
 * Every condition this object is WEARING right now, as keyword ids, in the order applied.
 *
 * 🚨 **A CARD SHOWED ONE STATUS OUT OF MANY.** `card.ts` drew `Taunting` and nothing else, so a
 * granted buff, a stun or a silence changed how a creature behaved with no mark on it anywhere. The
 * engine tracked them the whole time. `revival-mechanics`: **"CORRECT AND INVISIBLE" IS THE FAILURE
 * NOBODY OWNS** - a rule a player cannot perceive is worth less than one that is wrong and obvious.
 *
 * ⭐ **THE ARRAY IS ITS OWN FILTER, so there is no hand-written list of "which effects are
 * conditions".** `AppliedEffects` holds what is stuck ON the object, not what it can DO: a
 * battlecry creature's array is empty (measured - it is why the role plinth read every creature as
 * ATTACK when it derived roles from here). Anything still in it is, by definition, still applied.
 * A list I typed would be `engineering-invariants` §3b.1, a snapshot of someone else's system
 * frozen as a constant, and it would silently stop covering the next effect anybody adds.
 *
 * ⚠️ `Disabled` IS THE HALF THAT GETS FORGOTTEN, and it is why this cannot be a bare `.map()`. The
 * engine's own test is `HasAppliedNonDisabledEffect`: Silence and Stasis leave the effect attached
 * and switch it off. Drawing it anyway would tell a player a silenced creature is still taunting.
 *
 * ⚠️ **DISTINCT, because two copies of one buff are one condition to a reader.** The VALUE is not
 * on the runtime entry, so a second badge would say nothing a first one did not.
 */
export function liveConditions(obj: RuntimeObject | undefined): string[] {
  const applied = obj?.AppliedEffects as AppliedEffect[] | undefined;
  if (!Array.isArray(applied)) return [];
  const out: string[] = [];
  for (const e of applied) {
    if (e?.Disabled || !e?.ConfigId) continue;
    const kw = EFFECT_KEYWORD[e.ConfigId];
    if (kw && kw !== "None" && !out.includes(kw)) out.push(kw);
  }
  return out;
}

/**
 * The legal targets for a `GenericAttack` by `attackerId`, as the engine computes them.
 *
 * ⭐ **A LITERAL TRANSCRIPTION OF `Revival.Core`'s `CheckTaunt`**, which reads:
 *
 * ```cs
 * if (effectData.Keyword != EffectKeyword.GenericAttack) return true;      // spells ignore taunt
 * if (executor.HasAppliedNonDisabledEffect(EffectKeyword.IgnoreTaunting)) return true;
 * var taunters = pool.GetCardsFilterBy(RuntimeState.InTable, target.OwnerUserId)
 *                    .Where(c => c.HasAppliedNonDisabledEffect(EffectKeyword.Taunting));
 * if (taunters.Any()) return taunters.Contains(target);                    // ShouldTargetTaunt
 * ```
 *
 * ⚠️ **THE HERO IS SUBJECT TO IT TOO**, and that is the whole of Alex's question. The engine filters
 * by the TARGET'S OWNER, so while a taunter stands, the enemy hero is not a legal target either.
 * That is what "the cards protect the shield" means - and it is a KEYWORD, not a blanket rule:
 * a creature without Taunt guards nothing.
 *
 * ⚠️ **STEALTH IS A SEPARATE, ADDITIVE FILTER** (`InvalidAction.CantTargetStealth`): a stealthed
 * creature cannot be targeted at all - including while it is itself taunting, which is why the
 * stealth filter is applied AFTER the taunt set is chosen, never before.
 */
export function legalAttackTargets(
  model: MatchModel,
  attackerId: number,
  defenderUserId: string | undefined,
): Set<number> {
  const legal = new Set<number>();
  if (defenderUserId === undefined) return legal;

  const attacker = model.objects.get(attackerId);
  const board = model.zone(defenderUserId, "InTable");
  const hero = model.hero(defenderUserId);

  const targetable = (o: RuntimeObject | undefined): boolean =>
    !!o && typeof o.Id === "number" &&
    // Stealth and Immune remove a card from every target list.
    !hasKeyword(o, "Stealthing") &&
    !hasKeyword(o, "Immune");

  const candidates: RuntimeObject[] = board.filter(targetable);
  if (targetable(hero) && hero) candidates.push(hero);

  const taunters = board.filter((c) => hasKeyword(c, "Taunting"));
  const ignores = hasKeyword(attacker, "IgnoreTaunting");

  const allowed = taunters.length > 0 && !ignores
    // ⚠️ INTERSECTED with the targetable set, not substituted for it: a taunter that is also
    // stealthed is protected from being hit, and does not become the only legal target.
    ? candidates.filter((c) => taunters.some((t) => t.Id === c.Id))
    : candidates;

  for (const c of allowed) {
    if (typeof c.Id === "number") legal.add(c.Id);
  }
  return legal;
}

/**
 * THE RESTRICTION LEDGER - every keyword that changes what a player may legally DO.
 *
 * 🚨 **THIS IS THE THING WHOSE ABSENCE COST ALEX THE WHACK-A-MOLE.** It is not documentation:
 * `tools/verify_mechanics_coverage.py` reads the served database, finds every keyword whose own
 * description restricts an action, and FAILS THE BUILD if it is not listed here. A keyword added by
 * a future balance patch cannot slip through, and neither can one I simply did not think of.
 *
 * `mirrored`  the client enforces it, so an illegal move is never offered.
 * `server`    the engine enforces it and the client cannot see it; the move is offered, refused,
 *             and the player is TOLD why. Being explicit is the point - a silent rejection is what
 *             makes a working game look broken.
 */
export const RESTRICTIONS: Readonly<Record<string, "mirrored" | "server">> = Object.freeze({
  Taunting: "mirrored",
  IgnoreTaunting: "mirrored",
  Stealthing: "mirrored",
  Immune: "mirrored",
  ImmuneToDamage: "server",
  ImmuneToKeyword: "server",
  Sleeping: "mirrored",
  SleepGain: "server",
  Fast: "mirrored",
  Stunning: "mirrored",
  Petrify: "server",
  Stasis: "server",
  Barricade: "server",
  Immortal: "server",
  Sanctification: "server",
  Mark: "server",
  Silence: "server",
});

/* ══ BOARD ROLES ═══════════════════════════════════════════════════════════════════════════════
 *
 * 🚨 **ALEX, 2026-08-18:** *"on the table when the card plays can we group cards that are attack, or
 * defend or spell or heal cards and let it be very clear to the player what it belongs to? So maybe
 * zones."* Then, settling the open question: *"zoning is not on the original game table, it will be
 * a new design feature for us ... I want you to use your skills and research and design and
 * implement it."*
 *
 * ⛔ **AND THE RESEARCH SAYS THE CARDS MUST NOT MOVE.** Measured in the served database, not
 * assumed: **10 of 171 cards carry adjacency effects** (`rally-adjacent-2-taunt` x5,
 * `heal-max-adjacent` x5). Position on the table is therefore MECHANICAL for those cards, and
 * reordering the row to group by role would silently change what they hit. That is a rules change
 * wearing a visual improvement's clothes, and it is exactly the trap the queue note warned about.
 *
 * ⭐ **SO A ZONE IS A COLOUR, NOT A PLACE.** Each occupied slot gets a role plinth; the eye groups by
 * colour along the row and gets what a spatial zone would have given, while every card stays exactly
 * where the engine put it.
 *
 * ⚠️ **DERIVED FROM APPLIED EFFECTS, NEVER FROM THE PRINTED CARD** - the same rule the taunt badge
 * already follows. Taunt is granted by `rally-adjacent-2-taunt` and stripped by a silence
 * (`The Second Death` silences the whole table every round), so a role read off the card database
 * would mark a silenced defender as a wall and miss every granted one.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

export type BoardRole = "defender" | "building" | "support" | "striker";

/**
 * Keywords that make a creature's job HELPING its own side rather than hitting the other.
 *
 * ⚠️ Taken from `effect-keywords.generated.ts`, which is generated from the engine. A hand-typed
 * list here would be a second owner of the vocabulary and would rot the first time a card is added.
 */
const SUPPORT_KEYWORDS = new Set([
  "Heal", "BuffAttack", "BuffHealth", "BuffArmor",
  "BuffReplaceAttack", "BuffReplaceHealth", "BuffReplaceMana", "BuffReplaceAttackLimit",
  "Draw", "DrawAoe", "ManaGain", "Resurrect", "GiveEffects",
]);

/** Does this object IMPOSE an effect with the given keyword - i.e. is this part of its job? */
function imposes(obj: RuntimeObject | undefined, match: (keyword: string) => boolean): boolean {
  const imposing = obj?.ImposingEffects;
  if (!Array.isArray(imposing)) return false;
  return imposing.some((id) => {
    const kw = EFFECT_KEYWORD[id];
    return !!kw && match(kw);
  });
}

/** Is anything currently stuck on this object at all? Empty means nothing applied AND nothing stripped. */
function hasAnyLiveEffect(obj: RuntimeObject | undefined): boolean {
  const applied = obj?.AppliedEffects as AppliedEffect[] | undefined;
  return Array.isArray(applied) && applied.some((e) => !e?.Disabled && !!e?.ConfigId);
}

/**
 * What this object is DOING on the table, right now.
 *
 * ⚠️ Order matters and is deliberate: a defender that also heals is a DEFENDER, because taunt is the
 * property that changes how the opponent must play. Roles are a reading aid, so the most
 * decision-relevant one wins rather than the most flattering.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🚨 **THE TWO EFFECT LISTS ANSWER DIFFERENT QUESTIONS, AND USING ONE FOR BOTH SHIPPED A LIE.**
 *
 * The first version of this read EVERY role off `AppliedEffects` under the rule *"derive from
 * applied effects, never from the printed card"*. That rule is **right for taunt and wrong for
 * everything else**, and the difference is not stylistic:
 *
 * | | granted / stripped at runtime? | so the honest source is |
 * |---|---|---|
 * | **taunt** | YES - `rally-adjacent-2-taunt` grants it, a silence disables it | `AppliedEffects` |
 * | **support** | NO - a battlecry heal fires once, at a TARGET, and leaves nothing behind | `ImposingEffects` |
 *
 * ⛔ **MEASURED, NOT ARGUED.** Read off the wire from a live `InitializeGame`, the dealt deck is
 * **8 attack / 4 defend / 6 support**. The board, deriving support from `AppliedEffects`, labelled
 * **8 of 8 creatures ATTACK** - because a support creature's effect lands on its target, so its own
 * applied list is empty and it fell through to the default.
 *
 * ⭐ **AND THIS IS THE EXPENSIVE PART.** Alex, 2026-08-18, on seeing a board of nothing but ATTACK:
 * *"we can see now if the cards are actually playing out as they should or we are just getting one
 * type of card and the game mechanism and card attributes are not actually working, which I have
 * always suspected is the fact."* **The engine was fine. The instrument was not.** A readout keyed
 * on a field that cannot express the answer does not return "unknown" - it returns the default,
 * every time, and reads as a finding about the game.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function roleOf(obj: RuntimeObject | undefined, type?: string): BoardRole {
  if (!obj) return "striker";

  // ⚠️ `Building` is a REAL third card type (7 of 171) that the renderer never knew about, so every
  // one of them drew as a creature with a 0 attack and a null health badge. It has no attack and no
  // health at all; treating it as a creature is why they looked broken.
  if (type === "Building") return "building";

  // 1. LIVE taunt wins outright - granted counts, and a silence correctly takes it away.
  if (hasKeyword(obj, "Taunting")) return "defender";

  // 2. A card whose own list is EMPTY has had nothing applied and nothing stripped, so its printed
  //    taunt is still the truth. (A silenced taunter is NOT empty - it carries the disabled taunt
  //    and the silence - so it falls through to attacker, which is exactly right.)
  if (!hasAnyLiveEffect(obj) && imposes(obj, (k) => k === "Taunting")) return "defender";

  // 3. Support is a fixed fact about the card, never granted and never stripped, so it is read from
  //    what the card imposes rather than from what happens to be sitting on it.
  if (imposes(obj, (k) => SUPPORT_KEYWORDS.has(k))) return "support";

  return "striker";
}

/**
 * The label a player reads. Short, because it sits under a card at slot size.
 *
 * ⚠️ **NO TRAILING S.** Alex, 2026-08-18: *"adding the S to the end of the word makes it poor
 * grammar and harder to understand."* `STRIKES`/`DEFENDS` read as a sentence fragment about the card
 * rather than as a category name, which is what a plinth is. And the words are HIS - *"cards that
 * are attack, or defend or spell or heal"* - not a synonym I preferred.
 */
export function roleLabel(role: BoardRole): string {
  return { defender: "DEFEND", building: "STRUCTURE", support: "SUPPORT", striker: "ATTACK" }[role];
}
